import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import DobEventPayload from '../../services/events/dobEventPayload';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { Provider } from '../../config';
import { AuthenticationContext } from '../../providers/AuthenticationContext';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

function DateOfBirthScreen(): JSX.Element {
  const { t } = useTranslation();
  const [dobValid, setDobValid] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  // By default, prefill the date of birth to today plus 1 second in the future to prevent the continue button from being enabled initially
  const [dob, setDob] = useState<dayjs.Dayjs>(dayjs().add(1, 'second'));
  const [locale, setLocale] = useState<string>('en-gb');

  const { flowSuccessful, flowFailed } = useContext(FlowConfigContext);
  const { getCurrentToken } = useContext(AuthenticationContext);

  useEffect(() => {
    const browserLocale: string = navigator.language.toLowerCase();
    // Default to en-gb locale for a sane DD/MM/YYYY date format
    dayjs.locale('en-gb');
    // If the user uses a US locale, use it for the MM/DD/YYYY format
    if (browserLocale === 'en-us') {
      dayjs.locale('en'); // dayjs uses 'en' for US
      setLocale('en');
    }
  }, []);

  const onDateChange = (date: dayjs.Dayjs | null) => {
    // We have a valid date that is in the past so enable continue button
    if (date && date.isValid() && date.unix() < dayjs().unix()) {
      setDobValid(true);
      setDob(date);
      return;
    }
    setDobValid(false);
  };

  const handleConfirm = async () => {
    const formattedDate = dob.format('DD/MM/YYYY');
    const tokenResult = await getCurrentToken();
    if (tokenResult.isFailed()) {
      tokenResult.logError();
      flowFailed({ provider: Provider.WEB });
      return;
    }
    flowSuccessful(new DobEventPayload(formattedDate, tokenResult.success()));
    return;
  };

  return (
    <Container>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        <TitleContainer>
          <h1 data-testid="DOB_CAPTURE_HEADER">{t('screens.dateOfBirth.capture.title')}</h1>
        </TitleContainer>

        <div className="text-lg leading-6 mt-2 mb-6" data-testid="DATE_OF_BIRTH_INPUT">
          <DatePicker
            slotProps={{
              layout: {
                sx: {
                  '& .MuiPickersDay-root': {
                    color: 'rgb(var(--color-primary))',
                  },
                  '& .Mui-selected': {
                    backgroundColor: 'rgb(var(--color-primary))',
                    color: 'rgb(var(--color-textParagraph))',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: 'rgb(var(--color-textParagraph))',
                    fontWeight: 'bold',
                  },
                  '& .MuiButton-text': {
                    color: 'rgb(var(--color-primary))',
                  },
                },
              },
            }}
            sx={{
              '& .MuiSvgIcon-root': {
                color: 'rgb(var(--color-primary))',
              },
              '& .MuiInputBase-input': {
                color: 'rgb(var(--color-text-paragraph))',
              },
              '& .MuiInputBase-input:focus': {
                outline: 0,
                border: 0,
                boxShadow: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(var(--color-primary)) !important',
              },
            }}
            defaultValue={dayjs()}
            disableFuture
            value={dob}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            showDaysOutsideCurrentMonth
            onChange={onDateChange}
          />
        </div>

        {open && <div className="h-48" />}

        <ButtonContainer>
          <Button style={ButtonStyle.DEFAULT} onClick={handleConfirm} disabled={!dobValid} dataTestid="CONTINUE_BUTTON">
            <span>{t('screens.dateOfBirth.capture.buttonLabel')}</span>
          </Button>
        </ButtonContainer>
      </LocalizationProvider>
    </Container>
  );
}

export default withRouteAnalytics(DateOfBirthScreen, Screens.DATE_OF_BIRTH_CAPTURE);
